export const LAUNCH_ATTRIBUTION_SOURCES = [
  'product_hunt',
  'paid',
  'referral',
  'direct',
  'unknown',
] as const;

export type LaunchAttributionSource = (typeof LAUNCH_ATTRIBUTION_SOURCES)[number];

export interface LaunchAttribution {
  source: LaunchAttributionSource;
  utmSource: string | null;
  utmCampaign: string | null;
  /** Used only to deterministically derive source before database persistence. */
  utmMedium: string | null;
  referrerHost: string | null;
  /** Never stores a raw referral code or invite token. */
  hasRef: boolean;
}

export interface StripePaidSubscription {
  stripeSubscriptionId: string | null;
  billingSource: string | null;
  planTier: string | null;
  status: string | null;
}

const MAX_UTM_SOURCE_LENGTH = 64;
const MAX_UTM_CAMPAIGN_LENGTH = 120;
const MAX_REFERRER_HOST_LENGTH = 255;
const PAID_UTM_MEDIUMS = new Set(['cpc', 'ppc', 'paid', 'paid_social', 'display', 'affiliate']);

function normalizeAttributionValue(value: string | null, maxLength: number): string | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized.length > maxLength || !/^[a-z0-9][a-z0-9._-]*$/.test(normalized)) {
    return null;
  }
  return normalized;
}

function normalizeReferrerHost(value: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
    return hostname && hostname.length <= MAX_REFERRER_HOST_LENGTH ? hostname : null;
  } catch {
    return null;
  }
}

/**
 * Captures only bounded first-touch values observable before signup. The database
 * accepts this shape once for a fresh cohort member; it never derives it later
 * from product telemetry or prose.
 */
export function captureLaunchAttribution(
  locationHref: string,
  documentReferrer: string,
): LaunchAttribution {
  let url: URL;
  try {
    url = new URL(locationHref);
  } catch {
    return {
      source: 'unknown',
      utmSource: null,
      utmCampaign: null,
      utmMedium: null,
      referrerHost: null,
      hasRef: false,
    };
  }

  const utmSource = normalizeAttributionValue(
    url.searchParams.get('utm_source'),
    MAX_UTM_SOURCE_LENGTH,
  );
  const utmCampaign = normalizeAttributionValue(
    url.searchParams.get('utm_campaign'),
    MAX_UTM_CAMPAIGN_LENGTH,
  );
  const utmMedium = normalizeAttributionValue(
    url.searchParams.get('utm_medium'),
    MAX_UTM_SOURCE_LENGTH,
  );
  const referrerHost = normalizeReferrerHost(documentReferrer);

  let source: LaunchAttributionSource;
  if (utmSource === 'product_hunt' || utmSource === 'producthunt') {
    source = 'product_hunt';
  } else if (utmMedium && PAID_UTM_MEDIUMS.has(utmMedium)) {
    source = 'paid';
  } else if (referrerHost || url.searchParams.has('ref')) {
    source = 'referral';
  } else if (utmSource || utmCampaign || utmMedium) {
    source = 'unknown';
  } else {
    source = 'direct';
  }

  return {
    source,
    utmSource,
    utmCampaign,
    utmMedium,
    referrerHost,
    hasRef: url.searchParams.has('ref'),
  };
}

export function isSameLaunchUser(
  cohortUserId: string,
  lifecycleUserId: string,
  subscriptionUserId: string,
): boolean {
  return cohortUserId === lifecycleUserId && lifecycleUserId === subscriptionUserId;
}

export function isNonEmptyLaunchStrategy(strategy: unknown): boolean {
  return (
    typeof strategy === 'object' &&
    strategy !== null &&
    !Array.isArray(strategy) &&
    Object.keys(strategy as Record<string, unknown>).length > 0
  );
}

export function isEligibleStripePaidSubscription(subscription: StripePaidSubscription): boolean {
  return (
    Boolean(subscription.stripeSubscriptionId) &&
    subscription.billingSource === 'stripe' &&
    (subscription.planTier === 'pro' || subscription.planTier === 'pro_max')
  );
}

export function normalizeStripeSubscriptionStatus(
  status: string,
): 'active' | 'inactive' | 'cancelled' | 'past_due' {
  switch (status) {
    case 'active':
      return 'active';
    case 'past_due':
    case 'unpaid':
      return 'past_due';
    case 'canceled':
      return 'cancelled';
    default:
      return 'inactive';
  }
}
